/**
 * Alignment hooks — CCA-M1/M2/M3/M4-readback
 *
 * useAlignmentCheck               — mutation: POST /api/v1/alignment/check
 * useAlignmentAcknowledge         — mutation: POST /api/v1/alignment/acknowledge (decisions)
 * useAlignmentAcknowledgeGoal     — mutation: POST /api/v1/alignment/acknowledge (goals, M3)
 * useAlignmentAcknowledgements    — query: GET /api/v1/alignment/acknowledgements (AC-CCA-28)
 * useCompanyContext                — query: GET /api/v1/alignment/context (CCA-M2 AC-CCA-23)
 */

import { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  RorosAlignmentService,
  type AlignmentCheckSubject,
} from '@/services/roros/alignment.service';

export function useAlignmentCheck() {
  return useMutation({
    mutationFn: ({
      subject,
    }: {
      subject: AlignmentCheckSubject;
    }) => RorosAlignmentService.check(subject),
  });
}

export function useAlignmentAcknowledge() {
  return useMutation({
    mutationFn: ({
      decisionId,
      principleId,
    }: {
      decisionId: string;
      principleId: string;
    }) => RorosAlignmentService.acknowledge(decisionId, principleId),
  });
}

/** CCA-M3 AC-CCA-27: Acknowledge a goal alignment conflict. */
export function useAlignmentAcknowledgeGoal() {
  return useMutation({
    mutationFn: ({
      goalId,
      principleId,
    }: {
      goalId: string;
      principleId: string;
    }) => RorosAlignmentService.acknowledgeGoal(goalId, principleId),
  });
}

/**
 * AC-CCA-28: Fetch prior acknowledged principleIds for an existing entity.
 *
 * - edit mode (subjectId provided): fetches from audit log, seeds acknowledgedPrincipleIds.
 * - create mode (no subjectId): skipped entirely — enabled: false, returns empty Set.
 *
 * On fetch failure: soft-fails (returns empty Set) so conflicts still show as un-acked.
 * No error surfaced to user — alignment is non-blocking.
 */
export function useAlignmentAcknowledgements(
  subjectId: string | null | undefined,
  subjectType: 'decision' | 'goal' | 'outcome',
) {
  const query = useQuery({
    queryKey: ['alignment-acknowledgements', subjectId, subjectType],
    queryFn: () => RorosAlignmentService.getAcknowledgements(subjectId!, subjectType),
    enabled: !!subjectId,
    staleTime: 30_000, // 30s — stale after edit session
    retry: false,       // soft-fail: don't retry on 404 (foreign entity) or network error
  });

  // On success: return a stable-reference Set of acknowledged principleIds.
  // Memoized on query.data so the reference is stable between renders —
  // prevents the seed useEffects in dialogs from looping (new Set() every
  // render would change the dep reference even when the data hasn't changed).
  // On failure / loading / create-mode: returns a stable empty Set.
  const seededIds = useMemo(
    () => new Set<string>(query.data?.acknowledgedPrincipleIds ?? []),
    [query.data],
  );

  return {
    seededIds,
    isLoading: !!subjectId && query.isLoading,
  };
}

/**
 * CCA-M2 AC-CCA-23: Fetch the broad GuidanceBundle for CompanyContextPanel.
 * Returns the bundle entries grouped by layer.
 * Empty bundle when org has no context configured.
 */
export function useCompanyContext() {
  const query = useQuery({
    queryKey: ['alignment-context'],
    queryFn: () => RorosAlignmentService.getContext(),
    staleTime: 60_000, // 1 min — context doesn't change frequently
  });

  return {
    bundle: query.data?.bundle ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
